import { Bit } from "./bit";
import { IdName } from "../types/interfaces";
import { IdNameLookup } from "../lookup/id-name-lookup";

export abstract class BitConverter {
	public static isSingleBit(num: number): boolean {
		let bitField = BitConverter.toBitField(num);
		return bitField.length === 1 && bitField[0] !== 0;
	}

	/**
	 * Converts a number to its bit representation array high to low (e.g. 8 -> [1,0,0,0])
	 */
	public static readonly toBitArray = (num: number) => {
		if (num == null || num === 0) {
			return [];
		}

		return num.toString(2).split("").map((t) => parseInt(t));
	}

	/**
	 * Converts a bit representation array high to low to its number (e.g. [1,0,0,0] -> 8)
	 */
	public static readonly fromBitArray = (numArr: number[]) => {
		if (numArr == null || numArr.length <= 0) {
			return 0;
		}
		let arrAsString = numArr.join("");
		return parseInt(arrAsString, 2);
	}

	/**
	 * Converts a number to its bit set representation array (e.g 7 -> [1,2,4])
	 */
	public static readonly toBitField = (num: number) => {
		if (num == null || num === 0) {
			return [];
		}

		let result: number[] = [];
		// ex. 5 -> [4,1]

		let numArr = BitConverter.toBitArray(num).reverse();

		for (let i = 0; i < numArr.length; i++) {
			if (numArr[i] === 1) {
				let bitAsNum = Math.pow(2, i);
				result.push(bitAsNum);
			}
		}

		return result;
	}

	/**
	 * Converts a bit set representation array to its number (e.g [1,2,4] -> 7)
	 */
	public static readonly fromBitField = (numArr: number[]): number => {
		// ex. [4,1] -> 5
		if (numArr == null || numArr.length <= 0) {
			return 0;
		}

		let result = 0;
		for (let value of numArr) {
			result = Bit.or(value, result);
		}
		return result;
	}

	/**
	 * Converts a number to a array representation by split each number part (e.g. 257 -> [2,5,7])
	 */
	public static readonly numbersConcatToArray = (num: number): number[] => {
		// ex. 254 -> [2,5,4]
		if (num > 0) {
			let numAsString = num.toString();
			let result: number[] = [];
			for (let numAschar of numAsString) {
				result.push(parseInt((numAschar)));
			}
			return result;
		}
		return [];
	}

	/**
	 * Converts a array representation of a number to its number (e.g. [2,5,7] -> 257)
	 */
	public static readonly arrayToNumberConcat = (numArr: number[]): number => {
		// [2,3,4] -> 234
		if (numArr && numArr.length > 0) {
			let result = "";
			for (let val of numArr) {
				result = result + val.toString();
			}
			return parseInt(result);
		}
		return 0;
	}

	/**
	 * Converts a observable bit set number to the seperated representation of its exponential numbers (e.g. 5 -> [1,4])
	 */
	public static readonly asComputedBitFieldArray = (observable: KnockoutObservable<number>, lookup?: IdNameLookup<IdName>): KnockoutComputed<number[]> => {
		return ko.pureComputed({
			read: () => {
				let curProperties: number = observable();
				let result: number[] = BitConverter.toBitField(curProperties);
				if (lookup) {
					// mixed items from lookup which are set, will be added to the list
					let curMixedItemIds = lookup.items()
						.filter((item) => item.id &&
							Bit.and(item.id, (item.id - 1)) !== 0 &&
							Bit.and(curProperties, item.id)).map((item) => item.id);
					result = result.concat(curMixedItemIds);
				}

				return result;
			},
			write: (value) => {
				let result: number = BitConverter.fromBitField(value);
				observable(result);
			}
		});
	}
}